// =============================================
// VeriFactu QR Data Generator
// AEAT compliance for Spanish invoice verification
// Uses Node.js crypto — no external dependencies
// =============================================

import { createHash } from 'crypto';

// =============================================
// Constants
// =============================================

const VERIFACTU_BASE_URL =
  'https://www2.agenciatributaria.gob.es/static_files/common/internet/dep/aplicaciones/es/aeat/tike/cont/ws/SusFacturaConsulta.html';

// =============================================
// Helpers
// =============================================

/**
 * Generate a SHA-256 hex hash for VeriFactu fingerprinting.
 *
 * Uses Node.js built-in crypto module (no external dependencies).
 *
 * @param data - String to hash
 * @returns Lowercase hex-encoded SHA-256 hash
 */
function sha256(data: string): string {
  return createHash('sha256').update(data, 'utf8').digest('hex');
}

/**
 * Format Date or ISO string to DD-MM-YYYY format required by AEAT.
 */
function formatDateForAEAT(fecha: Date | string): string {
  if (typeof fecha === 'string') {
    const parts = fecha.slice(0, 10).split('-');
    if (parts.length !== 3) {
      throw new Error(`Fecha inválida: "${fecha}". Formato esperado: YYYY-MM-DD`);
    }
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  const day = String(fecha.getDate()).padStart(2, '0');
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const year = fecha.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Escape special characters for XML/SVG attribute values.
 */
function escapeXmlAttr(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// =============================================
// QR Data Generator
// =============================================

/**
 * Generate the VeriFactu QR verification URL for a Spanish invoice.
 *
 * Constructs the AEAT validation URL with the required parameters:
 * - nif: Issuer tax ID
 * - numserie: Invoice series + number
 * - fecha: Invoice date (DD-MM-YYYY)
 * - importe: Total amount (2 decimals)
 * - huella: SHA-256 fingerprint (VeriFactu hash if available, else computed)
 *
 * The fingerprint (huella) is computed as SHA-256 of:
 * `${nif_emisor}${numero}${fecha_DD-MM-YYYY}${importe}`
 *
 * @param invoice - Invoice identification data
 * @returns Complete AEAT verification URL string
 *
 * @example
 * ```ts
 * const url = generateInvoiceQrData({
 *   numero: 'F-001',
 *   fecha_emision: '2026-01-15',
 *   nif_emisor: 'B12345678',
 *   total_factura: 1210.00,
 * });
 * // https://www2.agenciatributaria.gob.es/.../SusFacturaConsulta.html?nif=B12345678&numserie=F-001&fecha=15-01-2026&importe=1210.00&huella=abc123...
 * ```
 */
export function generateInvoiceQrData(invoice: {
  numero: string;
  fecha_emision: Date | string;
  nif_emisor: string;
  total_factura: number;
  verifactu_id?: string;
}): string {
  const fechaAEAT = formatDateForAEAT(invoice.fecha_emision);
  const importeStr = invoice.total_factura.toFixed(2);

  // Use provided VeriFactu hash or compute one from invoice data
  const huella = invoice.verifactu_id
    || sha256(`${invoice.nif_emisor}${invoice.numero}${fechaAEAT}${importeStr}`);

  // Build URL with encoded parameters
  const params = new URLSearchParams({
    nif: invoice.nif_emisor,
    numserie: invoice.numero,
    fecha: fechaAEAT,
    importe: importeStr,
    huella,
  });

  return `${VERIFACTU_BASE_URL}?${params.toString()}`;
}

// =============================================
// QR SVG Generator (minimal, no external deps)
// =============================================

/**
 * Generate a minimal SVG representation for QR code data.
 *
 * Since we cannot use an external QR code library, this generates
 * a placeholder SVG containing:
 * - Standard QR finder patterns in three corners (7×7 modules)
 * - Timing patterns between finders
 * - Data-derived deterministic pattern for the remaining area
 * - The encoded URL as a `data-qr` attribute for frontend rendering
 *
 * The frontend should use a proper QR rendering library (e.g. qrcode.react)
 * for production display. This SVG is suitable for previews and fallbacks.
 *
 * @param data - The data string to encode (typically the VeriFactu URL)
 * @param size - SVG width/height in pixels (default: 200)
 * @returns SVG markup string with QR-like visual pattern
 *
 * @example
 * ```ts
 * const qrUrl = generateInvoiceQrData({ ... });
 * const svg = generateQrSvg(qrUrl, 250);
 * // Use svg in HTML: <div dangerouslySetInnerHTML={{ __html: svg }} />
 * ```
 */
export function generateQrSvg(data: string, size: number = 200): string {
  const encodedData = escapeXmlAttr(data);

  // QR Version 1 is 21×21 modules
  const gridSize = 21;
  const moduleSize = size / gridSize;
  const matrix = generatePlaceholderMatrix(data, gridSize);

  const modules: string[] = [];
  for (let y = 0; y < gridSize; y++) {
    for (let x = 0; x < gridSize; x++) {
      if (matrix[y][x]) {
        const px = x * moduleSize;
        const py = y * moduleSize;
        modules.push(
          `  <rect x="${px.toFixed(1)}" y="${py.toFixed(1)}" width="${moduleSize.toFixed(1)}" height="${moduleSize.toFixed(1)}" fill="#000"/>`
        );
      }
    }
  }

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" data-qr="${encodedData}">`,
    `  <rect width="${size}" height="${size}" fill="#fff"/>`,
    ...modules,
    '</svg>',
  ].join('\n');
}

// =============================================
// Internal Matrix Generation
// =============================================

/**
 * Generate a binary matrix for the QR placeholder SVG.
 *
 * Includes standard QR finder patterns in three corners and a
 * data-derived deterministic pattern in the center. This is NOT a valid
 * QR code — it serves as a visual placeholder only.
 *
 * @param data - Input data used to generate a deterministic visual pattern
 * @param gridSize - Module grid size (21 for QR Version 1)
 * @returns 2D boolean matrix where true = dark module
 */
function generatePlaceholderMatrix(data: string, gridSize: number): boolean[][] {
  // Initialize empty matrix
  const matrix: boolean[][] = Array.from({ length: gridSize }, () =>
    Array.from({ length: gridSize }, () => false)
  );

  // Draw 7×7 finder patterns in three corners
  drawFinderPattern(matrix, 0, 0);
  drawFinderPattern(matrix, 0, gridSize - 7);
  drawFinderPattern(matrix, gridSize - 7, 0);

  // Timing patterns (alternating modules between finder patterns)
  for (let i = 8; i < gridSize - 8; i++) {
    matrix[6][i] = i % 2 === 0;
    matrix[i][6] = i % 2 === 0;
  }

  // Generate data-based pattern using a simple hash
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    hash = ((hash << 5) - hash + data.charCodeAt(i)) | 0;
  }

  for (let y = 0; y < gridSize; y++) {
    for (let x = 0; x < gridSize; x++) {
      // Skip finder pattern areas (7×7 + 1-module separator)
      if (isFinderArea(y, x, gridSize)) continue;
      // Skip timing pattern rows/columns
      if (y === 6 || x === 6) continue;

      // Deterministic pattern from hash and position
      hash = ((hash << 5) - hash + (y * gridSize + x)) | 0;
      matrix[y][x] = (Math.abs(hash) % 3) === 0;
    }
  }

  return matrix;
}

/**
 * Check if a cell falls within a finder pattern area (including 1-module separator).
 */
function isFinderArea(row: number, col: number, gridSize: number): boolean {
  // Top-left finder + separator
  if (row <= 7 && col <= 7) return true;
  // Top-right finder + separator
  if (row <= 7 && col >= gridSize - 8) return true;
  // Bottom-left finder + separator
  if (row >= gridSize - 8 && col <= 7) return true;
  return false;
}

/**
 * Draw a 7×7 finder pattern at the specified position.
 *
 * Standard QR finder pattern: outer 7×7 border with inner 3×3 block.
 */
function drawFinderPattern(matrix: boolean[][], startRow: number, startCol: number): void {
  for (let r = 0; r < 7; r++) {
    for (let c = 0; c < 7; c++) {
      // Outer border
      const isOuter = r === 0 || r === 6 || c === 0 || c === 6;
      // Inner 3×3 block
      const isInner = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      matrix[startRow + r][startCol + c] = isOuter || isInner;
    }
  }
}

/** Adapter for API endpoints that pass (invoice, lines). */
export function generateQRData(
  invoice: Parameters<typeof generateInvoiceQrData>[0],
  _lines?: unknown
): string {
  return generateInvoiceQrData(invoice);
}
